'use client';

import React, { useState } from 'react';
import { SelectedSample } from '../Catalogue';
import { ButtonShadcn } from '@/components/ui/button-shadcn';
import { ChevronDown, ChevronUp, AlertTriangle, CheckCircle2, Music } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface CsvReviewViewProps {
  matched: SelectedSample[];
  unmatched: string[];
  onCancel: () => void;
  onProceed: () => void;
}

const CsvReviewView = ({ matched, unmatched, onCancel, onProceed }: CsvReviewViewProps) => {
  const { t } = useTranslation();
  const [showUnmatched, setShowUnmatched] = useState(false);

  const getAccessBadge = (sample: SelectedSample) => {
    if (sample.sample.isPrivate) {
      return (
        <span className="px-2 py-0.5 text-xs font-medium rounded bg-gray-600 text-gray-800">
          {t('adminCatalogue.access.hidden')}
        </span>
      );
    }
    const hasGroups = sample.sample.accessControl.allowedGroups.length > 0;
    const hasUsers = sample.sample.accessControl.allowedUsers.length > 0;
    if (hasGroups || hasUsers) {
      return (
        <span className="px-2 py-0.5 text-xs font-medium rounded bg-red-100 text-red-800">
          {t('adminCatalogue.access.restricted')}
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 text-xs font-medium rounded bg-green-100 text-green-800">
        {t('adminCatalogue.access.public')}
      </span>
    );
  };

  return (
    <div data-testid="csv-review-view" className="flex flex-col h-full">
      {/* Summary */}
      <div className="space-y-3 mb-4">
        <div className="flex items-center gap-2 text-green-700">
          <CheckCircle2 className="h-5 w-5" />
          <span className="text-sm font-medium">
            <span data-testid='csv-review-matched-count'>{matched.length}</span>
            <span>
              {t('adminCatalogue.csvUpload.matchedCount')}
            </span>
          </span>
        </div>

        {unmatched.length > 0 && (
          <div>
            <button
              onClick={() => setShowUnmatched(!showUnmatched)}
              className="flex items-center gap-2 text-amber-600 hover:text-amber-700 transition-colors cursor-pointer"
            >
              <AlertTriangle className="h-5 w-5" />
              <span className="text-sm font-medium">
                <span data-testid='csv-review-unmatched-count'>{unmatched.length}</span>
                <span>
                  {t('adminCatalogue.csvUpload.unmatchedCount')}
                </span>
              </span>
              {showUnmatched ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            {showUnmatched && (
              <div 
              className="mt-2 ml-7 p-3 bg-amber-50 rounded-md border border-amber-200 max-h-32 overflow-y-auto"
              data-testid='csv-review-unmatched-list'
              >
                <div className="text-xs text-amber-800 font-mono space-y-1">
                  {unmatched.map((id, i) => (
                    <span key={i}>{id}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Matched samples list */}
      <div className="border rounded-lg overflow-hidden flex-1 min-h-0 overflow-y-auto">
        <table className="w-full">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                {t('adminCatalogue.catalogueTable.title')}
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                {t('adminCatalogue.catalogueTable.access')}
              </th>
            </tr>
          </thead>
          <tbody data-testid="csv-review-matched-list">
            {matched.map((sample) => (
              <tr key={sample.id} className="border-t hover:bg-gray-50 transition-colors">
                <td className="px-4 py-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Music className="h-4 w-4 text-gray-400 shrink-0" />
                    <span data-testid={`csv-review-sample-title-${sample.id}`} className="text-gray-900">
                      {sample.sample.title}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-2">
                  {getAccessBadge(sample)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 mt-4">
        <ButtonShadcn
          data-testid="csv-review-cancel"
          variant="outline"
          size="sm"
          onClick={onCancel}
          className="cursor-pointer"
        >
          {t('adminCatalogue.common.cancel')}
        </ButtonShadcn>
        <ButtonShadcn
          data-testid="csv-review-proceed"
          variant="default"
          size="sm"
          onClick={onProceed}
          className="cursor-pointer"
        >
          {t('adminCatalogue.catalogueTable.manageAccess')}: {matched.length} {t('adminCatalogue.catalogueTable.selected')}
        </ButtonShadcn>
      </div>
    </div>
  );
};

export default CsvReviewView;
